import React, { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { FormApi } from 'final-form';
import { NumberField } from '~admin/view/components/ui/form/fields/field-number.component';
import { Column } from '~common/components/column.component';
import { Box } from '~common/components/box.component';
import { DatetimeField } from '~admin/view/components/ui/form/fields/field-datetime.component';
import { getOfferGroupPriceInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { Color } from '~common/model/basics';
import { Size } from '~common/components/button/button.helper';
import { Button } from '~common/components/button/button.component';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';
import { DeleteIcon } from '~web/view/components/ui/icon/delete.icon';
import { EventFormData } from '~admin/view/components/details/event-form-data';
import { MarketplaceType } from '~client/model/marketplace-type';
import { onlyOnePriceAllowed } from '~admin/view/components/details/offer-group-details/offer-group-fields.helper';

export interface OfferGroupPriceFormData {
  price: number;
  startDateTime: string;
  endDateTime: string;
}

interface PriceProps {
  name: string;
  index: number;
  marketplaceType: MarketplaceType;
  form?: FormApi<EventFormData>;
  onRemove?: (index: number) => void;
}

const Price: FunctionComponent<PriceProps> = ({ name, index, marketplaceType, form, onRemove }) => {
  const { t } = useTranslation();
  const showDeleteIcon = index !== 0;
  const disableAndResetDateFields = onlyOnePriceAllowed(marketplaceType);

  return (
    <Row>
      <RestrictBox>
        <StyledNumberField
          name={`${name}.price`}
          label={t('createEvent.offerGroupDetails.priceLabel')}
          startAdornment={'$'}
          decimal
        />
      </RestrictBox>
      <RestrictBox>
        <DatetimeField
          name={`${name}.startDateTime`}
          label={t('createEvent.marketplaceDetails.dateTime.startDateTime')}
          disabled={disableAndResetDateFields}
          reset={disableAndResetDateFields}
          form={form}
        />
      </RestrictBox>
      <RestrictBox>
        <DatetimeField
          name={`${name}.endDateTime`}
          label={t('createEvent.marketplaceDetails.dateTime.endDateTime')}
          disabled={disableAndResetDateFields}
          reset={disableAndResetDateFields}
          form={form}
        />
      </RestrictBox>
      <IconBox>
        {showDeleteIcon ? (
          <DeleteIcon
            color={Color.White}
            onClick={(): void => {
              onRemove?.(index);
            }}
          />
        ) : (
          <EmptyBox />
        )}
      </IconBox>
    </Row>
  );
};

interface OfferGroupPricesProps {
  fieldArrayName: string;
  marketplaceType: MarketplaceType;
  form?: FormApi<EventFormData>;
}

export const OfferGroupPrices: FunctionComponent<OfferGroupPricesProps> = ({
  fieldArrayName,
  marketplaceType,
  form,
}) => {
  const { t } = useTranslation();
  const disableAddPriceButton = onlyOnePriceAllowed(marketplaceType);

  return (
    <Container>
      <FieldArray name={`${fieldArrayName}.prices`}>
        {({ fields }): ReactNode => {
          const onRemove = (index: number): void => {
            fields.remove(index);
          };

          const onAdd = (): void => {
            fields.push(getOfferGroupPriceInitialValues());
          };

          return (
            <>
              {fields.map((name: string, index: number) => (
                <Price
                  key={index}
                  name={name}
                  index={index}
                  marketplaceType={marketplaceType}
                  form={form}
                  onRemove={onRemove}
                />
              ))}
              <ButtonContainer>
                <Button
                  color={Color.DimGray}
                  size={Size.Small}
                  onClick={onAdd}
                  disabled={disableAddPriceButton}
                >
                  <StyledPlusIcon width={12} height={12} />
                  {t('createEvent.offerGroupDetails.priceLabel')}
                </Button>
              </ButtonContainer>
            </>
          );
        }}
      </FieldArray>
    </Container>
  );
};

const Container = styled(Column)`
  width: 100%;
  border: 2px solid ${(props): string => props.theme.colors.gray.v3};
  border-radius: 16px;
  padding: 16px;
`;

const RestrictBox = styled(Box)`
  flex: 1 1 auto;
  margin: 0 12px;
`;

const Row = styled(Box)`
  width: 100%;
  margin-bottom: 18px;
`;

const ButtonContainer = styled(Box)`
  justify-content: flex-end;
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 4px;
`;

const IconBox = styled(Box)`
  flex: 0.1 0.1 auto;
  align-items: center;
  justify-content: center;
  margin: 0 12px;
`;

const EmptyBox = styled(Box)`
  width: 34px;
  height: 34px;
`;

const StyledNumberField = styled(NumberField)`
  max-width: 128px;
`;



// WEBPACK FOOTER //
// ./view/components/details/offer-group-details/offer-group-prices.component.tsx